import React, { useState, useEffect } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import supabase from '../supabase';

function WorkoutCalendar({ workouts }) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  // Map date → workout types
  const dayTypes = {};
  workouts.forEach(w => {
    const d = w.date?.slice(0, 10);
    if (d && w.type) dayTypes[d] = [...(dayTypes[d] || []), w.type];
  });

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const monthName = new Date(viewYear, viewMonth).toLocaleString('default', { month: 'long' });

  const prev = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const next = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

  return (
    <div className="d-card" style={{ marginBottom: '1.5rem', maxWidth: 420 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
        <button onClick={prev} style={navBtnStyle}>‹</button>
        <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{monthName} {viewYear}</span>
        <button onClick={next} style={navBtnStyle}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
          <div key={d} style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textAlign: 'center', paddingBottom: '3px', fontWeight: 600 }}>{d}</div>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={`empty-${i}`} />;
          const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const types = dayTypes[dateStr] || [];
          const worked = types.length > 0;
          const isToday = dateStr === todayStr;
          return (
            <div
              key={dateStr}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '3px 2px',
                borderRadius: 4,
                background: worked ? 'var(--accent, #0d6efd)' : isToday ? 'var(--surface-2, #2a2a2a)' : 'transparent',
                border: isToday && !worked ? '1px solid var(--accent, #0d6efd)' : '1px solid transparent',
                minHeight: 42,
              }}
            >
              <span style={{
                fontSize: '0.7rem',
                fontWeight: worked || isToday ? 600 : 400,
                color: worked ? '#fff' : isToday ? 'var(--text)' : 'var(--text-muted)',
                lineHeight: 1.4,
              }}>{day}</span>
              {types.map((t, idx) => (
                <span key={idx} style={{
                  fontSize: '0.55rem',
                  color: worked ? 'rgba(255,255,255,0.9)' : 'var(--text-muted)',
                  lineHeight: 1.2,
                  textAlign: 'center',
                  wordBreak: 'break-word',
                  maxWidth: '100%',
                }}>{t}</span>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const navBtnStyle = {
  background: 'none',
  border: 'none',
  color: 'var(--text-muted)',
  fontSize: '1.25rem',
  cursor: 'pointer',
  padding: '0 0.5rem',
  lineHeight: 1,
};

const ZONE_COLORS = { z1: '#4ade80', z2: '#60a5fa', z3: '#facc15', z4: '#f97316', z5: '#ef4444' };
const ZONE_LABELS = { z1: 'Z1', z2: 'Z2', z3: 'Z3', z4: 'Z4', z5: 'Z5' };

function WorkoutCard({ workout }) {
  const { type, date, duration_min, notes, metadata } = workout;
  const meta = metadata || {};
  const zones = meta.hr_zones;
  const totalZoneMin = zones ? Object.values(zones).reduce((a, b) => a + b, 0) : 0;
  const dateLabel = date ? new Date(date + 'T00:00:00').toLocaleDateString('default', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div className="d-card" style={{ marginBottom: '1rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '1rem' }}>{type || 'Workout'}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{dateLabel}</div>
        </div>
        {duration_min && (
          <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{duration_min} min</span>
        )}
      </div>

      {/* Notes */}
      {notes && (
        <p style={{ fontSize: '0.85rem', margin: '0.5rem 0', color: 'var(--text)' }}>{notes}</p>
      )}

      {/* Heart rate stats */}
      {(meta.avg_hr || zones) && (
        <div style={{ marginTop: '0.75rem' }}>
          {meta.avg_hr && (
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
              Avg HR: <strong style={{ color: 'var(--text)' }}>{meta.avg_hr} bpm</strong>
            </div>
          )}
          {zones && totalZoneMin > 0 && (
            <div>
              {/* Zone bar */}
              <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden', height: 8, marginBottom: '0.35rem' }}>
                {Object.entries(zones).map(([z, mins]) =>
                  mins > 0 ? (
                    <div key={z} title={`${ZONE_LABELS[z]}: ${mins} min`}
                      style={{ width: `${(mins / totalZoneMin) * 100}%`, background: ZONE_COLORS[z] }} />
                  ) : null
                )}
              </div>
              {/* Zone labels */}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {Object.entries(zones).map(([z, mins]) =>
                  mins > 0 ? (
                    <span key={z} style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: ZONE_COLORS[z], marginRight: 3 }} />
                      {ZONE_LABELS[z]} {mins}m
                    </span>
                  ) : null
                )}
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}

export default function Workouts() {
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchWorkouts() {
      const { data, error } = await supabase
        .from('workouts')
        .select('*')
        .order('date', { ascending: false })
        .limit(100);
      if (error) { setError(error.message); setLoading(false); return; }
      setWorkouts(data || []);
      setLoading(false);
    }
    fetchWorkouts();
  }, []);

  if (loading) return <div className="text-center mt-5"><Spinner animation="border" /></div>;

  return (
    <div>
      <div className="page-header">
        <h1>Workouts</h1>
        <p>{workouts.length} sessions</p>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <WorkoutCalendar workouts={workouts} />

      {workouts.length === 0 ? (
        <div className="d-card"><p style={{ color: 'var(--text-muted)', margin: 0 }}>No workouts logged.</p></div>
      ) : (
        workouts.map(w => <WorkoutCard key={w.id} workout={w} />)
      )}
    </div>
  );
}
